import React from 'react';
import { getJsonValueAtPath } from '@backstage/filter-predicates';
import type {
  FieldExtensionComponentProps,
  FieldSchema,
} from '@backstage/plugin-scaffolder-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';

import { ChoiceField } from './choice-field';
import { choiceStateOf, sourceKeyOf } from './choice-state';
import { normalizeDependsOn } from './depends-on';
import { isJsonValue } from './json-value';
import { useDependsOn } from './use-depends-on';

function identity(item: string): string {
  return item;
}

export interface DependentChoicePickerUiOptions {
  dependsOn: string | string[];
  enumMap: Record<string, string[]>;
  /** Max 256 characters. */
  sourcePath: string;
}

export const dependentChoicePickerSchema: FieldSchema<
  string,
  DependentChoicePickerUiOptions
> = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      dependsOn: z.union([z.string(), z.array(z.string())]),
      enumMap: z.record(z.array(z.string())),
      sourcePath: z.string().max(256),
    }),
});

export function DependentChoicePickerComponent(
  props: FieldExtensionComponentProps<string, DependentChoicePickerUiOptions>,
): React.ReactElement {
  const uiOptions = props.uiSchema['ui:options'];
  const dependsOn = uiOptions?.dependsOn;
  const sourcePath = uiOptions?.sourcePath ?? '';
  const enumMap = uiOptions?.enumMap ?? {};

  const state = useDependsOn(props, dependsOn);

  const firstDependency = normalizeDependsOn(dependsOn)[0];
  const sourceValue =
    state.kind === 'ready' && firstDependency !== undefined
      ? state.values[firstDependency]
      : undefined;
  const sourceValueJson = isJsonValue(sourceValue) ? sourceValue : undefined;
  const resolved = getJsonValueAtPath(sourceValueJson, sourcePath);
  const determiningValue = typeof resolved === 'string' ? resolved : undefined;
  const items =
    determiningValue === undefined ? [] : enumMap[determiningValue] ?? [];

  const selectedKey =
    typeof props.formData === 'string' ? props.formData : null;

  return (
    <ChoiceField
      description={props.schema.description}
      emptyText={
        determiningValue === undefined
          ? 'No choices'
          : `No choices for "${determiningValue}"`
      }
      getKey={identity}
      getLabel={identity}
      items={items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      required={props.required}
      selectedKey={selectedKey}
      selectedLabel={selectedKey}
      sourceKey={sourceKeyOf(state)}
      state={choiceStateOf(state)}
    />
  );
}

export const DependentChoicePicker: FormField = createFormField({
  component: DependentChoicePickerComponent,
  name: 'DependentChoicePicker',
  schema: dependentChoicePickerSchema,
});
